/**
 * The attraction field — the income side of the material economy (issue #122).
 *
 * `meta/crafting.ts` is a complete material *sink* and `run.inventory.materials`
 * is a complete pouch. Until now exactly one thing in the whole game filled it:
 * `combat/harvest.ts`, gated on carrying a scythe. Every other run had recipes
 * it could read, prices it could quote, and no way to earn a single unit. This
 * file is the world-facing half: a field that pulls loose cells toward a point,
 * so that matter already lying on the floor can be picked up.
 *
 * ===========================================================================
 * THE FIELD DOES NOT DAMAGE ANYTHING, AND THAT IS THE DESIGN
 * ===========================================================================
 *
 * The first sketch had the field damage the surrounding area as it pulled —
 * a vacuum that also chewed the walls. It is cut, and it must stay cut, for a
 * reason that is not about feel:
 *
 *   **Damaging terrain creates loose material, so a gathering tool that damages
 *   manufactures its own supply.** Hold, break rock, gather the debris, release,
 *   repeat. `CellWorld.damageCell` turns GRANITE into GRAVEL and GLASS into
 *   GRAVEL; GRAVEL is `Category.LOOSE`; loose is exactly what this field takes.
 *   One button would therefore be a closed loop with no input and no cost, and
 *   every material sink in the game — all ten recipes, the pouch cap, the
 *   opportunity cost `POUCH_CAP` exists to create — would be free. The economy
 *   would have an income side that is also its own mine.
 *
 * The second reason is the one this repo has already paid for once. A field
 * that gathers *and* breaks is two verbs on one input, which is #100 exactly:
 * `down` both selected the heavy attack and aimed it, and the fix that finally
 * worked was to **separate the verbs**, not to arbitrate between them
 * (`tests/downOwnership.test.ts` is the ruling). A gathering field that also
 * damages cannot be tuned out of that hole; it can only be split out of it.
 *
 * So, concretely, and enforced by `tests/attraction.test.ts`:
 *
 *   - This file never calls `damageCell`, `damageCircle`, `transmute`,
 *     `addTemp`, `heatCircle` or `set` with anything but `VOID`, and a source
 *     scan in the test file fails if it ever does.
 *   - The only two cell writes here are `swap` (which conserves matter exactly)
 *     and `set(x, y, VOID)` on a cell the *consumer has already accepted into
 *     the pouch* — so nothing is destroyed, it is moved.
 *   - `Flag.STATIC` cells are refused. Rigid-body cells are written back into
 *     the grid carrying `STATIC` (`sim/rigid.ts`), and hauling one cell out of a
 *     crate is structural damage under another name.
 *   - `Category.RIGID` is refused. Terrain is not loose matter, and a field that
 *     pulled a wall in one cell at a time would be a digging tool.
 *
 * If damage ever comes back it is a different button, on purpose, and this
 * comment is the reason to argue with rather than the thing to delete.
 *
 * ===========================================================================
 * THE WORK QUOTA IS THE SHAPE, NOT A GUARD BOLTED ON AFTERWARDS
 * ===========================================================================
 *
 * #42 measured four awake rigid bodies peaking at **4,096 cells/tick against an
 * 8,192 cap**. A radius-60 disc is ~11,300 cells; at one tenth loose and moving
 * that is one button costing the entire rigid-body budget, every tick it is
 * held. `tests/sourceHygiene.test.ts` bars `src/` from reaching for a wall
 * clock, so "stop after 2 ms" is not available even if it were wanted — a
 * counted quota is the only legal shape, and it is also the right one, because
 * a quota is deterministic and a clock is not.
 *
 * The number is therefore *derived and then the radius is chosen to fit it*,
 * which is the opposite order from picking a radius that feels good:
 *
 *     ATTRACT_WORK_CEILING       2048   half of #42's measured 4,096 peak,
 *                                       a quarter of its 8,192 cap
 *     worst case at radius R     2 * discCells(R) + discCells(2)
 *     largest R that fits        18     (2 * 1009 + 13 = 2031 <= 2048)
 *
 * `ATTRACT_MAX_RADIUS` is 18 because 19 does not fit, and for no other reason.
 * The measured throughput against it is in `tests/attraction.test.ts`, which
 * prints steady and worst-case units per tick the way `tests/frameBudget.test.ts`
 * does for the seven older systems.
 *
 * Work is counted the way `sim/charge.ts` counts it — per cell *examined*, not
 * per cell moved. A tick that scans 1,009 cells and moves none is not a free
 * tick, and `movedThisTick` alone would report one.
 *
 * ===========================================================================
 * THE SCAN GOES FROM THE CENTRE OUTWARD, AND THAT IS LOAD-BEARING
 * ===========================================================================
 *
 * Every cell steps at most one cell *inward* per tick, and the scan visits the
 * disc in order of increasing |dy| and then increasing |dx|. So a cell's
 * destination has already been visited this tick, always — which is what makes
 * the double-move impossible without `Flag.MOVED` and without a second clearing
 * pass. `sim/cellsim.ts` needs that flag because gravity moves cells *down*,
 * into rows it has not reached yet; a field that only moves cells toward its own
 * centre can order the scan so the problem does not arise.
 *
 * It also decides what a quota truncation costs: the inner ring is served first
 * and the rim waits a tick. That is the right degradation — the matter about to
 * arrive keeps arriving — and it is deterministic, which a rotating cursor over
 * the rows would also have been but with a worse failure mode.
 */

import { clamp } from '../core/math';
import { profiler } from '../core/profiler';
import { Category, Flag, M, VOID, type MatId } from './materials';
import { CellWorld } from './world';

/**
 * Cells in a filled disc of integer radius `r`, counted the way the scan walks
 * it: one row per `dy`, each row exactly as wide as the circle.
 *
 * Exported because the quota is derived from it rather than typed in, and a
 * derivation nobody can re-run is a magic number with a comment attached.
 */
export function discCells(r: number): number {
  const ri = Math.floor(r);
  if (ri < 0) return 0;
  const r2 = ri * ri;
  let n = 0;
  for (let dy = -ri; dy <= ri; dy++) n += 2 * Math.floor(Math.sqrt(r2 - dy * dy)) + 1;
  return n;
}

/**
 * The budget this system is allowed to spend, before the radius is chosen.
 *
 * Half of #42's measured four-body peak (4,096 cells/tick) and a quarter of its
 * cap (8,192). Stated as a ceiling rather than as the quota so the assertion in
 * the test reads forwards: the derived quota must fit under this, not equal it.
 */
export const ATTRACT_WORK_CEILING = 2048;

/**
 * How close a cell has to get before the consumer is offered it.
 *
 * **This is the answer to "what happens at radius zero", and it is a decision
 * rather than an emergent accident.** A cell that reaches this disc is offered
 * to `onArrive`; if the consumer takes it, the cell leaves the world; if the
 * consumer refuses — a full pouch — the cell simply stays, and is offered again
 * next tick. Nothing piles, nothing is escrowed, nothing is destroyed.
 *
 * The alternative considered was **piling at the feet**: let arrived matter heap
 * up as real cells and collect the mound on release. It is more physical, it
 * gives the field a visible mouthful, and a mound you can stand on (or be buried
 * by) is a better object than a counter. It was rejected on two counts. First,
 * it needs an escrow that spans the hold, and an escrow either destroys matter
 * when the pouch turns out to be full at the end or has to know about the pouch
 * — and the pouch lives in `meta/`, which `src/sim/` may not import. Second,
 * `combat/harvest.ts` states the invariant this file also keeps: **the pouch
 * refuses before the world does.** Auto-collect on contact keeps it for free,
 * because the refusal happens before the cell is cleared. 2 cells is roughly the
 * standing footprint, so "at your feet" is literal.
 */
export const ATTRACT_COLLECT_RADIUS = 2;

/**
 * The largest the field may ever grow, in cells.
 *
 * Chosen by the quota and by nothing else: 18 is the largest radius whose
 * worst case — every cell in the disc loose *and* moving — fits under
 * `ATTRACT_WORK_CEILING`. 19 costs 2 * 1,085 + 13 = 2,183 and does not.
 *
 * It is deliberately not a feel number. `BREACH_RADIUS` is 7 and `BLINK_RANGE`
 * is 62, so this sits between a dig and a teleport, which is a defensible place
 * for a gathering field to sit — but if it read badly the honest fix is to raise
 * the ceiling with a measurement behind it, not to raise the radius.
 */
export const ATTRACT_MAX_RADIUS = 18;

/**
 * The derived quota: every cell in the disc examined, every one of them moving,
 * plus the collect disc's own sweep.
 *
 * `tests/attraction.test.ts` asserts this is <= `ATTRACT_WORK_CEILING`, which is
 * the assertion that fails if anyone raises `ATTRACT_MAX_RADIUS` without going
 * back to #42's numbers first.
 */
export const ATTRACT_MAX_WORK_PER_TICK =
  2 * discCells(ATTRACT_MAX_RADIUS) + discCells(ATTRACT_COLLECT_RADIUS);

/**
 * Phases in the strength dither, and it must stay a power of two — the gate is
 * a mask, not a modulo.
 */
const PHASES = 8;

/**
 * Temperature at which a cell counts as incoming hazard.
 *
 * 200 C is below WOOD's 250 C ignition point on purpose: the warning has to
 * arrive before the thing it warns about, and a field is exactly the mechanism
 * that brings a 1,150 C lava cell across a room.
 */
const HAZARD_TEMP = 200;

/**
 * A field that pulls loose cells toward a point, and does nothing else.
 *
 * Positioned and sized by its owner every tick (`combat/gather.ts`); stepped
 * once per simulation tick from `core/game.ts`, after `rigid.writeBack()` so
 * that rigid-body cells are present in the grid and carry `Flag.STATIC` for
 * this pass to refuse. Between `rigid.lift()` and `rigid.writeBack()` those
 * cells are not in the grid at all, and a field stepped there would haul a
 * crate's WOOD away one cell at a time without ever touching a rigid body.
 */
export class AttractField {
  private world: CellWorld;

  /** Is the field open this tick? Set by `open`, cleared by `close`. */
  active = false;
  /** Centre, in cells. */
  cx = 0;
  cy = 0;
  /** Current radius in cells, 0..`ATTRACT_MAX_RADIUS`. */
  radius = 0;
  /** 0..1. Scales how often a cell in range takes its one inward step. */
  strength = 0;

  /**
   * Work units spent this tick: one per cell examined, one more per cell
   * actually moved. Read by the budget fixture, the same way `Charge`,
   * `Pressure`, `Supports` and the rest expose theirs.
   */
  workThisTick = 0;
  readonly maxWorkPerTick: number;

  /** Cells that took an inward step this tick. */
  movedThisTick = 0;
  /** Cells the consumer accepted and this pass cleared, this tick. */
  gatheredThisTick = 0;
  /** Cells examined this tick, whatever came of them. */
  examinedThisTick = 0;

  /**
   * Nearest hazardous cell **that this field is actually able to move**, and
   * what it is made of. `Infinity` / `VOID` when there is none.
   *
   * This is the whole of "it will vacuum lava, and that should stay". The field
   * does *not* filter by material: filtering is the safe option and the boring
   * one, and vacuuming a lava pool into your own feet is the most interesting
   * thing this mechanic does — it is what makes the growing radius a decision
   * instead of "always hold to maximum". But a field that silently pulls death
   * into your lap is a gotcha, and the difference between a gotcha and a choice
   * you got wrong is **whether you could see it coming**. So the hazard is
   * reported the tick it enters the radius, and `combat/gather.ts` turns that
   * into a line before it arrives. The travel time is the warning's margin:
   * a cell steps at most one cell per two ticks at full strength, so the rim of
   * a maximum field is over half a second away.
   *
   * Deliberately measured only over *eligible* cells. Hot granite in the wall is
   * not incoming; it is scenery, and warning about it would train the player to
   * ignore the warning.
   */
  hazardDist = Infinity;
  hazardMat: MatId = VOID;
  /** Eligible hazardous cells inside the radius this tick. */
  hazardCount = 0;

  /**
   * Offered every cell that reaches the collect disc. Return `true` to take it
   * — the field then clears the cell — or `false` to leave it standing.
   *
   * A callback rather than a pouch reference, for the same reason `Charge.onArc`
   * is one: `src/sim/` may not import `meta/`, and the refusal that keeps matter
   * from being destroyed has to happen on the pouch's side of that line.
   */
  onArrive: ((mat: MatId, x: number, y: number) => boolean) | null = null;

  constructor(world: CellWorld, maxWorkPerTick = ATTRACT_MAX_WORK_PER_TICK) {
    this.world = world;
    this.maxWorkPerTick = maxWorkPerTick;
  }

  /** Open or re-aim the field. Radius is clamped; the quota depends on it. */
  open(cx: number, cy: number, radius: number, strength: number): void {
    this.active = true;
    this.cx = Math.floor(cx);
    this.cy = Math.floor(cy);
    this.radius = clamp(radius, 0, ATTRACT_MAX_RADIUS);
    this.strength = clamp(strength, 0, 1);
  }

  close(): void {
    this.active = false;
    this.radius = 0;
    this.strength = 0;
  }

  step(): void {
    this.workThisTick = 0;
    this.movedThisTick = 0;
    this.gatheredThisTick = 0;
    this.examinedThisTick = 0;
    this.hazardDist = Infinity;
    this.hazardMat = VOID;
    this.hazardCount = 0;
    if (!this.active) return;

    this.collect();
    this.pull();

    // `profiler.setCount` and not `begin`/`end`: `SystemKey` lives in
    // `core/profiler.ts` and adding a row to the F3 overlay is a change to that
    // file's enumeration, which is not this file's to make. The count is what
    // the budget fixture reads either way.
    profiler.setCount('attract', this.workThisTick);
  }

  /**
   * Matter this field is allowed to move.
   *
   * Powders, liquids and sludge — the three categories that are *loose matter
   * lying around*. Everything else is refused for a stated reason:
   *
   *   - `RIGID` is terrain. Pulling it cell by cell is digging, and digging is
   *     the damage this mechanic must not do (see the header).
   *   - `GAS` rises, and rising gas is a hazard the game already uses. A field
   *     that also inhaled smoke would turn a real threat into ambience, and it
   *     would spend the quota on cells nobody wants.
   *   - `PLASMA` is fire and void-matter. Neither is something a pouch holds.
   *   - `Flag.STATIC` is "pinned, do not simulate". `sim/rigid.ts` stamps it on
   *     every cell it writes back, and level geometry carries it so that a sand
   *     floor does not settle out from under the room. Both readings say the
   *     same thing here: a pinned cell is structure, and vacuuming the floor a
   *     player is standing on is the damage this file refuses to do.
   *   - `M.indestructible` matches the refusal `CellWorld.damageCell` makes.
   *     **Defensive today**: ADAMANT_SEAL is the only indestructible material in
   *     the table and it is rigid, so the line above already covers it. It stays
   *     because the two refusals should read alike, and `tests/attraction.test.ts`
   *     holds a two-way check that fails the day a non-rigid material is made
   *     indestructible — at which point this stops being defensive.
   *
   * ==== WHAT THE RIGID REFUSAL COSTS A CORPSE, MEASURED (#131) ============
   *
   * #131 reported that this refuses "the material it was written for", because
   * `CORPSE_STONE` is declared `rigid(...)`. It was measured before it was
   * argued with, and the premise is false in both directions —
   * `tests/corpseGather.test.ts` is the measurement as assertions:
   *
   *   - **No enemy in the roster puddles into CORPSE_STONE.** `enemyData.ts`'s
   *     `corpseMaterial` scores LIQUID, SLUDGE and LOOSE a full point above
   *     everything else, so it picks the body's fluid part on purpose. 50 of the
   *     54 enemies leave a corpse this method accepts; driven end to end, 46 of
   *     54 actually bank matter. CORPSE_STONE reaches the world as biome scenery
   *     (`world/biomes.ts` places it as a shell and a secondary) and as *debris*
   *     from exactly one boss.
   *   - **No death rite produces it either.** A rite walks the tissue palette
   *     along `ashesInto`/`freezesInto`/`shatteredInto`, and CORPSE_STONE is not
   *     the product of any of the three for any material in the table.
   *
   * So the two blobs a death writes fall on opposite sides of this method by
   * construction, and that is the rule rather than an accident:
   *
   *     corpseMaterial   the body's soft part      soft  50/54   this takes it
   *     corpseDebris     its hardest part, warm    rigid 39/44   the swing's
   *     corpseDebris     the same part, shattered  loose 42/44   this takes it
   *
   * **Cold is the bridge.** Freeze an enemy and `killEnemy` writes the debris'
   * `shatteredInto` instead, which is grit — so the same `hasStatus(FROZEN)`
   * predicate that picks the `rime` rite and prices `shatterFrozen` also decides
   * which of the two gathering verbs owns a body's bones. Widening this method
   * to accept RIGID would delete that rule along with the digging refusal.
   */
  private eligible(mat: MatId, flags: number): boolean {
    if (mat === VOID) return false;
    if ((flags & Flag.STATIC) !== 0) return false;
    if (M.indestructible[mat] === 1) return false;
    const cat = M.category[mat];
    return cat === Category.LOOSE || cat === Category.LIQUID || cat === Category.SLUDGE;
  }

  /** The disc at the centre. See `ATTRACT_COLLECT_RADIUS` for why this exists. */
  private collect(): void {
    if (this.onArrive === null) return;
    const w = this.world;
    const r = ATTRACT_COLLECT_RADIUS;
    const r2 = r * r;
    for (let dy = -r; dy <= r; dy++) {
      const hw = Math.floor(Math.sqrt(r2 - dy * dy));
      for (let dx = -hw; dx <= hw; dx++) {
        if (this.workThisTick >= this.maxWorkPerTick) return;
        this.workThisTick++;
        this.examinedThisTick++;
        const x = this.cx + dx;
        const y = this.cy + dy;
        const mat = w.peek(x, y);
        if (!this.eligible(mat, w.getFlags(x, y))) continue;
        // The pouch refuses before the world does — `combat/harvest.ts`'s
        // invariant, kept here by asking first and clearing second.
        if (!this.onArrive(mat, x, y)) continue;
        w.set(x, y, VOID);
        this.gatheredThisTick++;
      }
    }
  }

  /**
   * One inward step for everything in range that the dither lets move.
   *
   * Rows are walked 0, +1, -1, +2, -2, … and each row is walked 0, +1, -1, … so
   * that a cell's destination is always a cell this sweep has already passed.
   * See the header: that ordering is what makes the double-move impossible.
   */
  private pull(): void {
    const w = this.world;
    const r = Math.floor(this.radius);
    if (r <= ATTRACT_COLLECT_RADIUS) return;
    const r2 = r * r;
    const collect2 = ATTRACT_COLLECT_RADIUS * ATTRACT_COLLECT_RADIUS;
    // 1..PHASES/2 — at full strength a cell steps every other tick, which is
    // what buys the hazard warning its margin. See `hazardDist`.
    const gate = 1 + Math.round(this.strength * (PHASES / 2 - 1));
    const tick = w.tick;

    for (let k = 0; k <= 2 * r; k++) {
      const dy = signedRing(k);
      const hw = Math.floor(Math.sqrt(r2 - dy * dy));
      for (let j = 0; j <= 2 * hw; j++) {
        if (this.workThisTick >= this.maxWorkPerTick) return;
        const dx = signedRing(j);
        this.workThisTick++;
        this.examinedThisTick++;
        const x = this.cx + dx;
        const y = this.cy + dy;
        const mat = w.peek(x, y);
        if (mat === VOID) continue;
        const flags = w.getFlags(x, y);
        if (!this.eligible(mat, flags)) continue;

        const d2 = dx * dx + dy * dy;
        if (this.hazardous(mat, flags, x, y)) {
          this.hazardCount++;
          const d = Math.sqrt(d2);
          if (d < this.hazardDist) {
            this.hazardDist = d;
            this.hazardMat = mat;
          }
        }

        // Already home. `collect` owns this disc; pulling here as well would
        // grind cells against the centre and spend work forever.
        if (d2 <= collect2) continue;
        if (((x * 3 + y * 5 + tick) & (PHASES - 1)) >= gate) continue;

        const ax = Math.abs(dx);
        const ay = Math.abs(dy);
        // Step on the dominant axis, both when they are close. A pure diagonal
        // for every off-axis cell makes the field draw an X; this makes it draw
        // a disc closing in.
        let tx = x;
        let ty = y;
        if (ax > 0 && ax * 2 >= ay) tx = x - Math.sign(dx);
        if (ay > 0 && ay * 2 >= ax) ty = y - Math.sign(dy);
        if (tx === x && ty === y) continue;
        if (!this.canEnter(mat, tx, ty)) continue;

        this.workThisTick++;
        w.swap(x, y, tx, ty);
        this.movedThisTick++;
      }
    }
  }

  /**
   * Can a cell of `mat` step into (tx, ty)?
   *
   * Empty space always. Otherwise only through *lighter* loose matter, which is
   * the same displacement rule the falling-sand step uses — so gravel sinks
   * through water on its way in and water does not climb through gravel. Rigid
   * terrain is never entered, which is the other half of "the field does not
   * dig": a wall stops the pull dead rather than being pushed aside.
   */
  private canEnter(mat: MatId, tx: number, ty: number): boolean {
    const w = this.world;
    const dm = w.peek(tx, ty);
    if (dm === VOID) return true;
    if (!this.eligible(dm, w.getFlags(tx, ty))) return false;
    return M.density[dm] < M.density[mat];
  }

  /** Is this cell something the player would regret having pulled in? */
  private hazardous(mat: MatId, flags: number, x: number, y: number): boolean {
    if ((flags & Flag.BURNING) !== 0) return true;
    if (this.world.getTemp(x, y) >= HAZARD_TEMP) return true;
    if (M.corrosiveness[mat] > 0) return true;
    return M.toxicity[mat] > 0;
  }
}

/**
 * 0, +1, -1, +2, -2, … — the centre-outward walk both loops use.
 *
 * A free function rather than two copies of `(k + 1) >> 1` with a sign, because
 * the ordering is a correctness property (see the header) and a correctness
 * property that appears twice gets changed once.
 */
export function signedRing(k: number): number {
  if (k <= 0) return 0;
  const step = (k + 1) >> 1;
  return (k & 1) === 1 ? step : -step;
}
